import { useSelector } from 'react-redux'
import { useEffect } from 'react'
import getFeatureEditor from '@chegg-wt/core-wa/utils/getEditor'
import {
  getFeedbackPosition,
  removePreviousHighlight,
} from '@chegg-wt/core-wa/utils/helpers'
import {
  FeedbackType,
  GrammarFeedback,
  ParaphraseFeedback,
} from '@chegg-wt/core-wa/types'
import { SentenceOffset } from '@chegg-wt/core-wa/state/types'
import { isPaperCheckInProgressSelector } from '@chegg-wt/core-wa/state/selectors'

type UseHighlightActiveCardArgs = {
  activeCardId: string
  feedbacks: GrammarFeedback[] | ParaphraseFeedback[]
  setActiveCard: (cardId: string) => void
  sentenceOffsets: SentenceOffset[]
}

/**
 * useHighlightActiveCard - handles only grammar & paraphrase cards
 */
export const useHighlightActiveCard = ({
  activeCardId,
  feedbacks,
  setActiveCard,
  sentenceOffsets,
}: UseHighlightActiveCardArgs) => {
  const isPaperCheckInProgress = useSelector(isPaperCheckInProgressSelector)

  useEffect(() => {
    // intialize the active card with index zero on first load, and update active card to the first index
    if (
      feedbacks.length > 0 &&
      (!activeCardId ||
        (feedbacks[0].feedbackType === FeedbackType.GRAMMAR && // if first feedback is grammar, then remaining will also be grammar
          isPaperCheckInProgress &&
          activeCardId !== feedbacks[0].id))
    ) {
      const feedbackId = feedbacks[0].id
      setActiveCard(feedbackId)
      return
    }

    const feedback = feedbacks.find((feedback) => feedback.id === activeCardId)
    if (!feedback) return

    const { offset, length } = getFeedbackPosition(feedback, sentenceOffsets)

    const editor = getFeatureEditor()
    const { from, to } = editor.state.selection
    editor.commands.setCustomHighlight({
      id: feedback.id,
      type: feedback.feedbackType,
      offset,
      length,
    })

    editor.commands.setTextSelection({ from, to })
    // on leaving screen or switching tab
    return () => {
      removePreviousHighlight(feedbacks, activeCardId, sentenceOffsets)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [feedbacks, sentenceOffsets, setActiveCard, activeCardId])
}
